import React, {useState} from 'react'
import {flowResult} from 'mobx'
import {observer} from 'mobx-react-lite'
import {useDisclosure} from '../../vanilla'
import {useBasketStore} from '../../store/hooks/useStore'
import {AddAllToCartConfirmation} from './add-all-to-cart-confirmation'
import {AddAllToCartMain} from './add-all-to-cart-main'
import {useAddAllToBasketParams} from './use-add-all-to-basket-params'
import {useListenToAddAllToBasketLinks} from './use-listen-to-add-all-to-basket-links'
import {useRestoreInitialHref} from './use-restore-initial-href'
import {PartialProductItem} from '../../types/store/basket'
import {useCustomerStore} from '../../store/hooks/useStore'
import {useLocation} from 'react-router-dom'
import {redirectIfNotRegistered} from '../../utils/login-redirect-utils'
import useNavigation from '../../hooks/use-navigation'

const AddAllToCartContainer: React.FC = () => {
  const {products, confirmation, updateParams} = useAddAllToBasketParams()
  const basketStore = useBasketStore()
  const addAllToBasketModal = useDisclosure(true)
  const confirmationModal = useDisclosure(false)
  const [processing, setProcessing] = useState(false)
  const restoreInitialHref = useRestoreInitialHref()

  // Re-direct if customer is not logged in
  const navigate = useNavigation()
  const {isRegistered, loading: loadingCustomer} = useCustomerStore()
  // Construct the url in the right format to reopen the modal / automatically add the items
  const {pathname} = useLocation()
  const productIds = products?.available?.map(i => i.id).join(',')
  const quantities = products?.available?.map(i => i.quantity).join(',')
  const params = new URLSearchParams()
  if (confirmation) params.set("confirmation", "yes")
  if (productIds) params.set("products", productIds)
  if (quantities) params.set("quantities", quantities)
  const path = `${pathname}?${params.toString()}`

  useListenToAddAllToBasketLinks({
    update: updateParams,
    openModal: addAllToBasketModal.onOpen
  })

  if (products == null) {
    return null
  }

  const addAllToCart = async () => {
    if (!products?.available?.length) {
      return
    }
    const availableProducts = products.available.map((product) => {
      return {
        product: product,
        quantity: product.quantity,
      }
    })
    setProcessing(true)
    await flowResult(basketStore.updateBasketItemQuantity(availableProducts as PartialProductItem[]))
    setProcessing(false)
  }

  const handleAdd = async () => {
    if (!redirectIfNotRegistered({navigate, pathname: path, isRegistered, loadingCustomer})) {
      if (confirmation) {
        confirmationModal.onOpen()
      } else {
        await addAllToCart()
        addAllToBasketModal.onClose()
      }
    }
  }

  const handleConfirm = async () => {
    await addAllToCart()
    confirmationModal.onClose()
  }

  return (
    <>
      <AddAllToCartMain
        onCancel={() => {
          restoreInitialHref()
          addAllToBasketModal.onClose()
        }}
        products={products}
        loading={processing}
        onAdd={handleAdd}
        {...addAllToBasketModal}
      />
      <AddAllToCartConfirmation
        loading={processing}
        onConfirm={handleConfirm}
        onCancel={() => {
          confirmationModal.onClose()
          addAllToBasketModal.onOpen()
        }}
        {...confirmationModal}
      />
    </>
  )
}

export const AddAllToCart = observer(AddAllToCartContainer)
